import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  Check,
  X,
  Minus,
  Shield,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Eye,
  Plus,
  Settings,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

// TES-1546: the per-role permission literals below are intentionally repetitive
// static config; the prettier reformat (a11y aria-label fix) re-surfaces them as a
// clone group without adding any new duplication. Pre-existing, not in scope.
// fallow-ignore-file code-duplication

type Permission = "full" | "limited" | "none";

interface AreaActions {
  view?: Permission;
  create?: Permission;
  manage?: Permission;
  execute?: Permission;
}

interface RolePermissionsData {
  role: string;
  permissions: {
    checklists: { view: Permission; manage: Permission };
    inspections: { view: Permission; create: Permission; execute: Permission };
    defects: { view: Permission; create: Permission; manage: Permission };
    schedules: { view: Permission; manage: Permission };
    positions: { view: Permission; manage: Permission };
    pdfProfiles: { view: Permission; manage: Permission };
    history: { view: Permission };
    userManagement: { view: Permission; manage: Permission };
    teams: { view: Permission; manage: Permission };
    tasks: { view: Permission; manage: Permission };
    canBeAssigned: boolean;
  };
}

const rolePermissionsData: Record<string, RolePermissionsData["permissions"]> = {
  admin: {
    checklists: { view: "none", manage: "none" },
    inspections: { view: "none", create: "none", execute: "none" },
    defects: { view: "none", create: "none", manage: "none" },
    schedules: { view: "none", manage: "none" },
    positions: { view: "none", manage: "none" },
    pdfProfiles: { view: "none", manage: "none" },
    history: { view: "limited" },
    userManagement: { view: "full", manage: "full" },
    teams: { view: "full", manage: "full" },
    tasks: { view: "none", manage: "none" },
    canBeAssigned: false,
  },
  qualitaetsmanager: {
    checklists: { view: "full", manage: "full" },
    inspections: { view: "full", create: "full", execute: "limited" },
    defects: { view: "full", create: "full", manage: "full" },
    schedules: { view: "full", manage: "full" },
    positions: { view: "full", manage: "full" },
    pdfProfiles: { view: "full", manage: "full" },
    history: { view: "full" },
    userManagement: { view: "none", manage: "none" },
    teams: { view: "full", manage: "none" },
    tasks: { view: "limited", manage: "limited" },
    canBeAssigned: true,
  },
  tester: {
    checklists: { view: "none", manage: "none" },
    inspections: { view: "limited", create: "full", execute: "limited" },
    defects: { view: "limited", create: "full", manage: "limited" },
    schedules: { view: "none", manage: "none" },
    positions: { view: "none", manage: "none" },
    pdfProfiles: { view: "none", manage: "none" },
    history: { view: "limited" },
    userManagement: { view: "none", manage: "none" },
    teams: { view: "limited", manage: "none" },
    tasks: { view: "limited", manage: "limited" },
    canBeAssigned: true,
  },
  mangelbearbeiter: {
    checklists: { view: "none", manage: "none" },
    inspections: { view: "none", create: "none", execute: "none" },
    defects: { view: "limited", create: "none", manage: "limited" },
    schedules: { view: "none", manage: "none" },
    positions: { view: "none", manage: "none" },
    pdfProfiles: { view: "none", manage: "none" },
    history: { view: "limited" },
    userManagement: { view: "none", manage: "none" },
    teams: { view: "limited", manage: "none" },
    tasks: { view: "limited", manage: "limited" },
    canBeAssigned: true,
  },
  customer_approver: {
    checklists: { view: "limited", manage: "none" },
    inspections: { view: "limited", create: "none", execute: "none" },
    defects: { view: "limited", create: "none", manage: "none" },
    schedules: { view: "none", manage: "none" },
    positions: { view: "limited", manage: "none" },
    pdfProfiles: { view: "limited", manage: "none" },
    history: { view: "none" },
    userManagement: { view: "limited", manage: "none" },
    teams: { view: "limited", manage: "none" },
    tasks: { view: "none", manage: "none" },
    canBeAssigned: true,
  },
};

const ActionBadge = React.forwardRef<
  HTMLDivElement,
  {
    action: "view" | "create" | "manage" | "execute";
    permission: Permission;
    label: string;
    tooltip: string;
  }
>(({ action, permission, label, tooltip, ...props }, ref) => {
  if (permission === "none") return null;

  const colorClasses =
    permission === "full"
      ? "bg-success/10 text-success border-success/20"
      : "bg-warning/10 text-warning border-warning/20";

  const Icon = action === "view" ? Eye : action === "create" ? Plus : Settings;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          ref={ref}
          variant="outline"
          className={cn("gap-1 cursor-help text-xs", colorClasses)}
          {...props}
        >
          <Icon className="h-3 w-3" />
          {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
});
ActionBadge.displayName = "ActionBadge";

interface AreaWithActions {
  areaKey: string;
  areaLabel: string;
  actions: AreaActions;
}

// TES-1546: pre-existing God-component cognitive complexity (~30), unchanged by the
// a11y aria-label fix; prettier reformat re-surfaces it on the new-only fallow gate.
// fallow-ignore-next-line complexity
export function UserPermissionsCard() {
  const { t } = useTranslation();
  const { roles, isAdmin, isQualitaetsmanager } = useAuth();
  const [isOpen, setIsOpen] = useState(true);
  const canViewRoleMatrix = isAdmin || isQualitaetsmanager;

  // Get highest priority role permissions
  const userRole = roles.includes("admin")
    ? "admin"
    : roles.includes("qualitaetsmanager")
      ? "qualitaetsmanager"
      : roles.includes("tester")
        ? "tester"
        : roles.includes("mangelbearbeiter")
          ? "mangelbearbeiter"
          : roles.includes("customer_approver")
            ? "customer_approver"
            : null;

  if (!userRole) return null;

  const permissions = rolePermissionsData[userRole];
  const roleLabel = t(`rolePermissions.roles.${userRole}.label`);

  // Build list of accessible areas with their specific actions
  const areasWithActions: AreaWithActions[] = [];

  // Helper to check if any action is accessible
  const hasAccess = (actions: AreaActions) =>
    Object.values(actions).some((p) => p !== "none" && p !== undefined);

  // Checklists
  if (hasAccess(permissions.checklists)) {
    areasWithActions.push({
      areaKey: "checklists",
      areaLabel: t("rolePermissions.areas.checklists"),
      actions: { view: permissions.checklists.view, manage: permissions.checklists.manage },
    });
  }

  // Inspections
  if (hasAccess(permissions.inspections)) {
    areasWithActions.push({
      areaKey: "inspections",
      areaLabel: t("rolePermissions.areas.inspections"),
      actions: {
        view: permissions.inspections.view,
        create: permissions.inspections.create,
        execute: permissions.inspections.execute,
      },
    });
  }

  // Defects
  if (hasAccess(permissions.defects)) {
    areasWithActions.push({
      areaKey: "defects",
      areaLabel: t("rolePermissions.areas.defects"),
      actions: {
        view: permissions.defects.view,
        create: permissions.defects.create,
        manage: permissions.defects.manage,
      },
    });
  }

  // Schedules
  if (hasAccess(permissions.schedules)) {
    areasWithActions.push({
      areaKey: "schedules",
      areaLabel: t("rolePermissions.areas.schedules"),
      actions: { view: permissions.schedules.view, manage: permissions.schedules.manage },
    });
  }

  // Positions
  if (hasAccess(permissions.positions)) {
    areasWithActions.push({
      areaKey: "positions",
      areaLabel: t("rolePermissions.areas.positions"),
      actions: { view: permissions.positions.view, manage: permissions.positions.manage },
    });
  }

  // PDF Profiles
  if (hasAccess(permissions.pdfProfiles)) {
    areasWithActions.push({
      areaKey: "pdfProfiles",
      areaLabel: t("rolePermissions.areas.pdfProfiles"),
      actions: { view: permissions.pdfProfiles.view, manage: permissions.pdfProfiles.manage },
    });
  }

  // History
  if (hasAccess(permissions.history)) {
    areasWithActions.push({
      areaKey: "history",
      areaLabel: t("rolePermissions.areas.history"),
      actions: { view: permissions.history.view },
    });
  }

  // User Management
  if (hasAccess(permissions.userManagement)) {
    areasWithActions.push({
      areaKey: "userManagement",
      areaLabel: t("rolePermissions.areas.userManagement"),
      actions: { view: permissions.userManagement.view, manage: permissions.userManagement.manage },
    });
  }

  // Teams
  if (hasAccess(permissions.teams)) {
    areasWithActions.push({
      areaKey: "teams",
      areaLabel: t("rolePermissions.areas.teams"),
      actions: { view: permissions.teams.view, manage: permissions.teams.manage },
    });
  }

  // Tasks
  if (hasAccess(permissions.tasks)) {
    areasWithActions.push({
      areaKey: "tasks",
      areaLabel: t("nav.tasks"),
      actions: { view: permissions.tasks.view, manage: permissions.tasks.manage },
    });
  }

  const getActionTooltip = (action: string, permission: Permission) => {
    const accessType =
      permission === "full"
        ? t("rolePermissions.fullAccessTooltip")
        : t("rolePermissions.limitedAccessTooltip");
    return accessType;
  };

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle className="text-base">{t("dashboard.yourPermissions")}</CardTitle>
            </div>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label={t("dashboard.togglePermissions")}
              >
                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
          </div>
          <CardDescription>
            {t("dashboard.yourRole")}:{" "}
            <span className="font-medium text-foreground">{roleLabel}</span>
          </CardDescription>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
            <div className="space-y-3">
              {/* Legend */}
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pb-2 border-b">
                <div className="flex items-center gap-1">
                  <Check className="h-3 w-3 text-success" />
                  <span>{t("rolePermissions.fullAccess")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Minus className="h-3 w-3 text-warning" />
                  <span>{t("rolePermissions.limitedAccess")}</span>
                </div>
                <div className="border-l pl-4 flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <Eye className="h-3 w-3" />
                    <span>{t("rolePermissions.view")}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Plus className="h-3 w-3" />
                    <span>{t("rolePermissions.create")}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Settings className="h-3 w-3" />
                    <span>{t("rolePermissions.manage")}</span>
                  </div>
                </div>
              </div>

              {/* Areas with specific actions */}
              <TooltipProvider>
                <div className="space-y-2">
                  {areasWithActions.map(({ areaKey, areaLabel, actions }) => (
                    <div key={areaKey} className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium min-w-[120px]">{areaLabel}:</span>
                      <div className="flex flex-wrap gap-1">
                        {actions.view && actions.view !== "none" && (
                          <ActionBadge
                            action="view"
                            permission={actions.view}
                            label={t("rolePermissions.view")}
                            tooltip={getActionTooltip("view", actions.view)}
                          />
                        )}
                        {actions.create && actions.create !== "none" && (
                          <ActionBadge
                            action="create"
                            permission={actions.create}
                            label={t("rolePermissions.create")}
                            tooltip={getActionTooltip("create", actions.create)}
                          />
                        )}
                        {actions.execute && actions.execute !== "none" && (
                          <ActionBadge
                            action="manage"
                            permission={actions.execute}
                            label={t("rolePermissions.execute")}
                            tooltip={getActionTooltip("execute", actions.execute)}
                          />
                        )}
                        {actions.manage && actions.manage !== "none" && (
                          <ActionBadge
                            action="manage"
                            permission={actions.manage}
                            label={t("rolePermissions.manage")}
                            tooltip={getActionTooltip("manage", actions.manage)}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </TooltipProvider>

              {/* Assignable status */}
              <div className="pt-2 border-t text-sm text-muted-foreground">
                {permissions.canBeAssigned ? (
                  <span className="flex items-center gap-1">
                    <Check className="h-4 w-4 text-success" />
                    {t("dashboard.canReceiveAssignments")}
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <X className="h-4 w-4 text-muted-foreground" />
                    {t("dashboard.cannotReceiveAssignments")}
                  </span>
                )}
              </div>

              {/* Link to full role matrix - only for admin */}
              {canViewRoleMatrix && (
                <div className="pt-2 border-t">
                  <Link
                    to="/admin/roles"
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    {t("dashboard.viewFullRoleMatrix")}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
